import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	EventEmitter,
	Input,
	OnInit,
	Output,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { asapScheduler } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import {
	ClassificationType,
	IClassification,
} from '@app/pages/cmms/interfaces/classification.interfaces';
import { cmmsClassifications } from '@app/pages/cmms/state/cmms.selectors';
import { startClassificationLoad } from '@app/pages/cmms/state/cmms.actions';
import {
	CLASSIFICATIONS_TYPES,
	ClassificationTypeItem,
} from '@app/pages/cmms/components/shared-form-group-controls/cmms-classification/classification-types';
import { ClassificationNamePipe } from '@app/pages/cmms/components/shared-form-group-controls/cmms-classification/classification-name-pipe';
import {
	ClassificationTreeComponentComponent,
	takeAllParentIdsNodes,
} from '@app/pages/cmms/components/shared-controls/classification-tree-component/classification-tree-component.component';
import { NgxTolgeeModule } from '@tolgee/ngx';
@Component({
	selector: 'cmms-classification',
	templateUrl: './cmms-classification.component.html',
	styleUrls: ['./cmms-classification.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		LetDirective,
		MatAutocompleteModule,
		MatButtonModule,
		MatExpansionModule,
		MatFormFieldModule,
		MatIconModule,
		MatTableModule,
		MatInputModule,
		MatMenuModule,
		MatSelectModule,
		ReactiveFormsModule,
		ClassificationNamePipe,
		ClassificationTreeComponentComponent,
		NgxTolgeeModule,
	],
})
export class CmmsClassificationComponent implements OnInit {
	readonly displayedColumns: readonly string[] = ['type', 'name'];

	// Classifications that user able to add and remove if not set allowedStandards
	readonly defaultSupportedClassifications: readonly ClassificationType[] = [
		'BCT',
		'CCB',
	];
	@Output() classificationsListChanged = new EventEmitter<IClassification[]>();
	classificationNameInputControl = new FormControl<string | IClassification>('');

	classificationStore: Signal<IClassification[]> = toSignal(
		this.store$.select(cmmsClassifications)
	);
	autocompleteInputChanged: Signal<string | IClassification> = toSignal(
		this.classificationNameInputControl.valueChanges.pipe(
			startWith(''),
			debounceTime(500),
			distinctUntilChanged()
		)
	);
	allowedStandardsValue: readonly ClassificationType[] =
		this.defaultSupportedClassifications;
	userEditableClassificationType: WritableSignal<ClassificationType> = signal(
		this.defaultSupportedClassifications[0]
	);

	isFooterRowVisible: WritableSignal<boolean> = signal<boolean>(false);
	attachedClassifications: WritableSignal<IClassification[]> = signal([]);
	filteredOptions: Signal<IClassification[]> = computed(() =>
		this.getFilteredOptions(
			this.classificationStore(),
			this.autocompleteInputChanged(),
			this.attachedClassifications(),
			this.userEditableClassificationType()
		)
	);

	constructor(private store$: Store) {
		effect(() => {
			const selectedClassificationType = this.userEditableClassificationType();
			// in case if you want to interact with store in angular signal effect
			// you need to use asapScheduler:
			// https://github.com/ngrx/platform/issues/3932
			asapScheduler.schedule(() => {
				this.store$.dispatch(
					startClassificationLoad({
						queryParams: {
							type: selectedClassificationType,
						},
					})
				);
			});
		});
	}

	@Input() set attachedClassification(classifications: IClassification[] | null) {
		if (!classifications) {
			return;
		}

		this.attachedClassifications.set(classifications);
	}

	@Input() set allowedStandards(value: ClassificationType[]) {
		this.allowedStandardsValue = value;
		this.userEditableClassificationType.set(value[0]);
	}

	get classificationsTypes(): ClassificationTypeItem[] {
		return CLASSIFICATIONS_TYPES.filter(ct =>
			this.allowedStandardsValue.includes(ct.type)
		);
	}

	ngOnInit(): void {
		this.store$.dispatch(
			startClassificationLoad({
				queryParams: { type: this.userEditableClassificationType() },
			})
		);
	}

	getFilteredOptions(
		allClassifications: IClassification[],
		classificationFilterValue: string | IClassification,
		attachedClassifications: IClassification[],
		classificationType: ClassificationType
	): IClassification[] {
		const selectedClassificationPks = {};
		attachedClassifications.forEach(classification => {
			selectedClassificationPks[classification.pk] = true;
		});

		if (!classificationFilterValue || typeof classificationFilterValue !== 'string') {
			return allClassifications;
		}

		const searchTextLower = classificationFilterValue.toLowerCase();
		const classification = allClassifications
			// if user apply search we do not need tree structure
			.filter(
				classification =>
					!!classification.name &&
					classification.name.toLowerCase().includes(searchTextLower) &&
					classification.type === classificationType
			);
		const filteredClassificationWithParentsPks = takeAllParentIdsNodes(
			classification.map(x => x.pk),
			allClassifications
		);
		return allClassifications.filter(x => filteredClassificationWithParentsPks.has(x.pk));
	}

	displayFn(item): string {
		return item?.name ? item.name : '';
	}

	applySelections() {
		const value = this.classificationNameInputControl.value;
		if (!value || typeof value === 'string') {
			return;
		}

		this.classificationNameInputControl.setValue('');
		const updatedClassifications = [value, ...this.attachedClassifications()];
		this.attachedClassifications.set(updatedClassifications);
		this.classificationsListChanged.emit(updatedClassifications);
	}

	removeItemFromSelections(value: string | IClassification) {
		if (!value || typeof value === 'string') {
			return;
		}

		const classifications = this.attachedClassifications();
		const updatedClassification = classifications.filter(
			classification => classification.pk !== value.pk
		);
		this.attachedClassifications.set(updatedClassification);
		this.classificationsListChanged.emit(updatedClassification);
	}
	classificationTypeName(classificationType: ClassificationType) {
		return CLASSIFICATIONS_TYPES.find(row => row['type'] === classificationType)['name'];
	}
}
